import { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Paper,
  Chip,
} from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
// eslint-disable-next-line @backstage/no-relative-monorepo-imports
import type {
  AssessmentDestination,
  TargetIntentDraft,
} from '../../../../../contracts/assess/target';
import {
  compatibleImageSubset,
  normalizeImageIntent,
  TargetIntentEditor,
} from './TargetIntentEditor';
import type { DestinationConnection } from './TargetIntentEditor';
import type { InstanceCandidate } from './InstanceSelectionTable';
import { intakeContext } from './intakeContext';

export type TargetIntents = Record<string, TargetIntentDraft>;
const emptyTargetIntents: TargetIntents = {};
function sourcePartition(region?: string): InstanceCandidate['partition'] {
  if (region?.startsWith('cn-')) return 'aws-cn';
  if (region?.startsWith('us-gov-')) return 'aws-us-gov';
  return 'aws';
}
type TargetProfile = {
  entityRef: string;
  provider: string;
  accountId?: string;
  region: string;
  vpcId?: string;
  subnetIds: string[];
  securityGroupIds: string[];
  /** Additive projection of the canonical approved destination, never browser-invented defaults. */
  destinationDefaults?: Partial<AssessmentDestination>;
};

export function HorizonTargetIntents(
  props: FieldExtensionComponentProps<TargetIntents>,
) {
  const fieldId = useId();
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const context = intakeContext(props);
  const scope = context.workloadRef as string | undefined;
  const targetRef = context.targetPlatformRef as string | undefined;
  const [sources, setSources] = useState<InstanceCandidate[]>([]);
  const [profile, setProfile] = useState<TargetProfile>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const sequence = useRef(0);
  const [bulkKeys, setBulkKeys] = useState<string[]>([]);
  const [bulkSource, setBulkSource] = useState('');
  const [reviewBulk, setReviewBulk] = useState(false);
  const [connections, setConnections] = useState<DestinationConnection[]>([]);
  const [connectionError, setConnectionError] = useState('');
  const values = props.formData ?? emptyTargetIntents;
  useEffect(() => {
    const current = ++sequence.current;
    setSources([]);
    setProfile(undefined);
    setError('');
    setBulkKeys([]);
    setBulkSource('');
    setReviewBulk(false);
    setBusy(false);
    if (!scope) return undefined;
    setBusy(true);
    const json = async (service: string, path: string) => {
      const base = await discovery.getBaseUrl(service);
      const response = await fetchApi.fetch(`${base}${path}`);
      if (!response.ok)
        throw new Error(
          `Authorized intake context unavailable (${response.status}); refresh the source/target selection.`,
        );
      return response.json();
    };
    void Promise.all([
      scope.startsWith('asscope-')
        ? json(
            'horizon-assess',
            `/v1/cloud/scopes/${encodeURIComponent(scope)}`,
          )
        : Promise.resolve({
            children: [{ resourceRef: scope, instanceId: scope }],
          }),
      targetRef
        ? json('horizon-source', '/v1/targets')
        : Promise.resolve({ targets: [] }),
    ])
      .then(([snapshot, targets]) => {
        if (current !== sequence.current) return;
        if (
          !Array.isArray(snapshot.children) ||
          !snapshot.children.every(
            (child: any) =>
              typeof child.resourceRef === 'string' &&
              typeof child.instanceId === 'string',
          )
        )
          throw new Error('Invalid frozen selection response');
        setSources(
          snapshot.children.map((child: any) => ({
            key: child.resourceRef,
            partition: sourcePartition(child.region),
            instanceId: child.instanceId,
            connectionId: child.connectionId ?? '',
            accountId: child.accountId ?? '',
            region: child.region ?? '',
            networkId: child.networkId ?? '',
            name: child.displayName,
            architecture: child.sourceMetadata?.architecture,
            sourceOs: child.sourceMetadata?.sourceOs,
            launchImageId: child.sourceMetadata?.launchImageId,
          })),
        );
        const selected = targets.targets?.find(
          (item: TargetProfile) => item.entityRef === targetRef,
        );
        if (targetRef && !selected)
          throw new Error(
            'Approved target no longer available; existing intent is retained for explicit review.',
          );
        setProfile(selected);
      })
      .catch(caught => {
        if (current === sequence.current)
          setError(
            caught instanceof Error
              ? caught.message
              : 'Intake context unavailable',
          );
      })
      .finally(() => {
        if (current === sequence.current) setBusy(false);
      });
    return () => {
      sequence.current = current + 1;
    };
  }, [scope, targetRef, discovery, fetchApi, attempt]);

  useEffect(() => {
    let active = true;
    setConnectionError('');
    void discovery
      .getBaseUrl('horizon-assess')
      .then(base => fetchApi.fetch(`${base}/v1/cloud/accounts`))
      .then(async response => {
        if (!response.ok)
          throw new Error(
            'Destination connections could not be loaded. Enter destination details and retry connection selection.',
          );
        const result = await response.json();
        if (
          !Array.isArray(result.accounts) ||
          !result.accounts.every(
            (item: DestinationConnection) =>
              typeof item.id === 'string' &&
              typeof item.accountId === 'string' &&
              typeof item.status === 'string',
          )
        )
          throw new Error('Invalid destination connection response.');
        if (active)
          setConnections(
            result.accounts.map((item: DestinationConnection) => ({
              id: item.id,
              accountId: item.accountId,
              title:
                typeof item.title === 'string' ? item.title : 'AWS account',
              status: item.status,
            })),
          );
      })
      .catch(caught => {
        if (active)
          setConnectionError(
            caught instanceof Error
              ? caught.message
              : 'Destination connections unavailable.',
          );
      });
    return () => {
      active = false;
    };
  }, [discovery, fetchApi, attempt]);

  useEffect(() => {
    if (
      !busy &&
      !error &&
      sources.length &&
      !Object.keys(values).length &&
      !props.disabled &&
      !props.readonly
    ) {
      props.onChange(
        Object.fromEntries(
          sources.map(source => [source.key, normalizeImageIntent()]),
        ),
      );
    }
  }, [busy, error, sources, values, props]);

  const locked = Boolean(props.disabled || props.readonly || busy || error);
  const outdated = Object.keys(values).filter(
    key => !sources.some(source => source.key === key),
  );
  const canonical = (key: string, value: TargetIntentDraft) => {
    const next = { ...values };
    for (const source of sources)
      next[source.key] = normalizeImageIntent(values[source.key]);
    next[key] = normalizeImageIntent(value);
    props.onChange(next);
    setReviewBulk(false);
  };
  const selectedBulk = sources.filter(source => bulkKeys.includes(source.key));
  const donor = values[bulkSource];
  const canBulk =
    !!donor?.requestedAmiId &&
    compatibleImageSubset(selectedBulk) &&
    (!donor.desiredOs ||
      selectedBulk.every(
        source =>
          source.sourceOs?.family === donor.desiredOs!.family.toUpperCase(),
      ));
  const aws =
    !profile || ['aws', 'Amazon Web Services (AWS)'].includes(profile.provider);
  const defaults = profile
    ? {
        accountId: profile.accountId,
        region: profile.region,
        vpcId: profile.vpcId,
        ...(profile.subnetIds.length === 1
          ? { subnetId: profile.subnetIds[0] }
          : {}),
        ...(profile.securityGroupIds.length === 1
          ? { securityGroupIds: profile.securityGroupIds }
          : {}),
        ...profile.destinationDefaults,
      }
    : undefined;
  return (
    <Box mt={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gridGap={12}
      >
        <Typography variant="h5">Destination and target image</Typography>
        {!!sources.length && (
          <Chip
            size="small"
            label={`${sources.length} selected server${
              sources.length === 1 ? '' : 's'
            }`}
          />
        )}
      </Box>
      <Typography variant="body2" color="textSecondary">
        Enter a destination for each selected server, or leave it blank for
        source discovery. Custom AMIs are optional. Horizon will validate
        supplied details during assessment.
      </Typography>
      {!scope && (
        <Paper
          variant="outlined"
          style={{ padding: 24, marginTop: 16, borderRadius: 12 }}
        >
          <Typography variant="subtitle1">
            Choose the servers to assess
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Return to Source scope, select at least one server, then choose
            Confirm scope and continue. Destination and custom AMI fields will
            appear for those servers.
          </Typography>
        </Paper>
      )}
      {busy && <Typography role="status">Loading selected servers…</Typography>}
      {error && (
        <Box role="alert">
          <Typography color="error">{error}</Typography>
          <Button onClick={() => setAttempt(value => value + 1)}>
            Retry intake context
          </Button>
        </Box>
      )}
      {!aws && (
        <Typography color="error" role="alert">
          Custom AMI intent is AWS-only. Clear existing image and destination
          intents before submitting this non-AWS route; it is not executable
          through an AWS adapter.
        </Typography>
      )}
      {outdated.length > 0 && !busy && (
        <Box role="alert">
          <Typography>
            {outdated.length} target drafts belong to servers outside the
            current frozen selection. No scope expansion is allowed.
          </Typography>
          <Button
            disabled={locked}
            onClick={() =>
              props.onChange(
                Object.fromEntries(
                  sources.map(source => [
                    source.key,
                    normalizeImageIntent(values[source.key]),
                  ]),
                ),
              )
            }
          >
            Reconcile target drafts to exact selected scope
          </Button>
        </Box>
      )}
      {sources.map(source => (
        <Box key={source.key}>
          <TargetIntentEditor
            key={`${source.key}/${targetRef ?? ''}`}
            source={source}
            value={values[source.key]}
            onChange={value => canonical(source.key, value)}
            disabled={locked || !aws}
            connections={connections}
            connectionError={connectionError}
            destinationDefaults={defaults}
            allowedSubnetIds={profile?.subnetIds}
            allowedSecurityGroupIds={profile?.securityGroupIds}
          />
          {!aws &&
            (values[source.key]?.requestedAmiId ||
              values[source.key]?.destination) && (
              <Button
                disabled={locked}
                onClick={() => canonical(source.key, normalizeImageIntent())}
              >
                Clear AWS intent for {source.instanceId}
              </Button>
            )}
          <FormControlLabel
            label={`Include ${source.instanceId} in reviewed image subset`}
            control={
              <Checkbox
                disabled={locked || !aws}
                checked={bulkKeys.includes(source.key)}
                onChange={(_, checked) => {
                  setBulkKeys(previous =>
                    checked
                      ? [...previous, source.key]
                      : previous.filter(key => key !== source.key),
                  );
                  setReviewBulk(false);
                }}
              />
            }
          />
        </Box>
      ))}
      {sources.length > 1 && aws && (
        <Box mt={2}>
          <TextField
            id={`${fieldId}-bulk-source`}
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label="Copy image intent from selected server"
            value={bulkSource}
            disabled={locked}
            onChange={event => {
              setBulkSource(event.target.value);
              setReviewBulk(false);
            }}
          >
            <option value="">Choose an existing custom image intent</option>
            {sources
              .filter(source => values[source.key]?.requestedAmiId)
              .map(source => (
                <option key={source.key} value={source.key}>
                  {source.instanceId}
                </option>
              ))}
          </TextField>
          <Button
            disabled={locked || !canBulk}
            onClick={() => setReviewBulk(true)}
          >
            Review image application to selected subset ({selectedBulk.length})
          </Button>
          {!!selectedBulk.length && !canBulk && (
            <Typography>
              Bulk application requires an explicit image and matching evidenced
              OS family, distribution and architecture. Provider validation
              still applies.
            </Typography>
          )}
          {reviewBulk && canBulk && (
            <Box role="alert">
              <Typography>
                Apply {donor.requestedAmiId} only to:{' '}
                {selectedBulk.map(source => source.instanceId).join(', ')}.
                Destinations remain per-server; compatibility is not yet
                verified.
              </Typography>
              <Button
                disabled={locked}
                onClick={() => {
                  const next = Object.fromEntries(
                    sources.map(source => [
                      source.key,
                      normalizeImageIntent(values[source.key]),
                    ]),
                  );
                  for (const source of selectedBulk)
                    next[source.key] = normalizeImageIntent({
                      ...next[source.key],
                      requestedAmiId: donor.requestedAmiId,
                      desiredOs: donor.desiredOs,
                    });
                  props.onChange({ ...values, ...next });
                  setReviewBulk(false);
                }}
              >
                Confirm reviewed image subset
              </Button>
            </Box>
          )}
        </Box>
      )}
      {props.rawErrors?.map(message => (
        <Typography color="error" key={message}>
          {message}
        </Typography>
      ))}
    </Box>
  );
}
